import { ModuleType } from "entities/modules/types";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  Timestamp,
  query,
} from "firebase/firestore";
import { db } from "shared/api/firebase";
import { PageModuleData } from "../types";
import { copyImage } from "./copyImage";

type Params = {
  module: PageModuleData;
  sourceBrandId: string;
  sourcePageId: string;
  targetBrandId: string;
  targetPageId: string;
};

export const movePageModule = async (params: Params): Promise<void> => {
  const { module, sourceBrandId, sourcePageId, targetBrandId, targetPageId } =
    params;

  const sourceModuleRef = doc(
    db,
    `brands/${sourceBrandId}/pages/${sourcePageId}/modules/${module.id}`,
  );
  const sourceModuleDoc = await getDoc(sourceModuleRef);

  if (!sourceModuleDoc.exists()) {
    throw new Error(`Module with ID ${module.id} does not exist`);
  }

  const moduleData = sourceModuleDoc.data() as PageModuleData;

  const batch = writeBatch(db);

  const targetModulesCollection = collection(
    db,
    `brands/${targetBrandId}/pages/${targetPageId}/modules`,
  );

  const targetModulesQuery = query(targetModulesCollection);
  const targetModulesSnapshot = await getDocs(targetModulesQuery);

  targetModulesSnapshot.forEach((doc) => {
    const moduleData = doc.data() as PageModuleData;
    const newOrder = (moduleData.order || 0) + 1;

    batch.update(doc.ref, {
      order: newOrder,
      updatedAt: Timestamp.now(),
    });
  });

  const newTargetModuleRef = doc(targetModulesCollection);
  const newModuleId = newTargetModuleRef.id;
  const targetPath = `brands/${targetBrandId}/pages/${targetPageId}/modules/${newModuleId}`;

  // Handle image paths for ContentBlocks and TopPrivacyTipsBlock
  let updatedModuleData = { ...moduleData };

  if (
    updatedModuleData.type === ModuleType.ContentBlocks &&
    updatedModuleData.contentBlocks
  ) {
    const contentBlockPromises = updatedModuleData.contentBlocks.map(
      async (block) => {
        if (block.backgroundImage) {
          const newImagePath = await copyImage(
            block.backgroundImage,
            targetPath,
          );
          return {
            ...block,
            backgroundImage: newImagePath,
          };
        }
        return block;
      },
    );

    updatedModuleData.contentBlocks = await Promise.all(contentBlockPromises);
  }

  if (updatedModuleData.topPrivacyTipsBlock?.backgroundImage) {
    const newImagePath = await copyImage(
      updatedModuleData.topPrivacyTipsBlock.backgroundImage,
      targetPath,
    );

    updatedModuleData.topPrivacyTipsBlock = {
      ...updatedModuleData.topPrivacyTipsBlock,
      backgroundImage: newImagePath,
    };
  }

  batch.set(newTargetModuleRef, {
    ...updatedModuleData,
    id: newModuleId,
    order: 1,
    updatedAt: Timestamp.now(),
  });

  batch.delete(sourceModuleRef);

  await batch.commit();
};
